import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom/client';
import _ from 'underscore';

import Primer3TabState from 'analyses/Primer3TabState';
import PrimerWizard from 'analyses/primers/PrimerWizard';
import PrimerWizardTabState from 'analyses/primers/PrimerWizardTabState';
import * as analytics from 'analytics/Analytics';
import * as biojs from 'BioJS';
import {MeltingTempMethod} from 'biojs/BioConstants';
import {Direction} from 'biojs/SequencePrimer';
import BufferOptionsView from 'buffers/BufferOptionsView';
import BufferTabView from 'buffers/BufferTabView';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import * as colors from 'lib/Colors';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import {Primer3Match} from 'molbio/MolBioConstants';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import preferences from 'Preferences';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import Spinner from 'ui-library/Spinner';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';

class PrimerWizardTabView extends BufferTabView<PrimerWizardTabState> {
  reactRoot: ReactDOM.Root | null = null;

  static override initClass(): void {
    this.prototype.className = 'buffer-tab';
  }

  override initialize(): void {
    const selection = this.model.get('sequence').get('selection');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(selection, 'change:start change:end', this.render);
  }

  getSelection(): {start: number; end: Optional<number>} {
    const sequence = this.model.get('sequence');
    const startIndex = sequence.startIndex();
    const selection = sequence.get('selection');
    const start = sequtil.modelStartToDisplay(selection.start(), startIndex);
    const end = sequtil.modelEndToDisplay(selection.end(), sequence.length(), startIndex);
    return {start, end};
  }

  override render(): this {
    this.reactRoot = renderToDOM(
      <PrimerWizard
        parameters={this.model.get('params')}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateParameters={(e: any) => {
          this.model.set({params: e});
          this.render();
        }}
        selection={this.getSelection()}
        showTmParameters={function () {
          legacyRunModal(MeltingTempModal, {});
        }}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        getDefault={this.model.getDefault}
      />,
      this.el,
      this.reactRoot
    );
    return this;
  }

  override removeAsync(callback: () => void): BufferTabView<PrimerWizardTabState> {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }
}

PrimerWizardTabView.initClass();

interface PrimerWizardOptionsProps {
  disabled: boolean;
  generatePrimers: () => void;
}

function PrimerWizardOptions(props: PrimerWizardOptionsProps): JSX.Element {
  return (
    <ul className="nav navbar-nav form-inline">
      <li className="pull-right">
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MINI}
          onClick={props.generatePrimers}
          disabled={props.disabled}
          text="Generate Primers"
          dataTestAttrs={{component: 'PrimerWizardTab', element: 'generatePrimersButton'}}
        />
      </li>
      {props.disabled ? (
        <li className="pull-right">
          <Spinner />
        </li>
      ) : undefined}
    </ul>
  );
}

/* eslint-disable @typescript-eslint/unbound-method */
class PrimerWizardOptionsView extends BufferOptionsView<PrimerWizardTabState> {
  declare working?: boolean;
  reactRoot: ReactDOM.Root | null = null;

  static initClass(): void {
    this.prototype.className = 'navbar';
  }

  override initialize(): void {
    this.addPrimers = this.addPrimers.bind(this);
    this.generatePrimers = this.generatePrimers.bind(this);
    this.working = false;
  }

  trackGeneratePrimersFail(error: string | null): void {
    analytics.track('primer', 'generate_primers_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      error,
      sequence_id: this.model.get('sequence').get('id'),
    });
  }

  addPrimers(res: Array<Primer3Match>): void {
    if (res.length === 0) {
      const errorMessage = 'No primers could be found - try adjusting your parameters.';
      this.trackGeneratePrimersFail(errorMessage);
      notifications.error(errorMessage);
    } else {
      const sequence = this.model.get('sequence');
      analytics.track('primer', 'generate_primers_success', {
        product: ANALYTICS_MODULE.MOLBIO,
        sequence_id: sequence.get('id'),
      });
      const primers = new Backbone.Collection(res);
      const filename = sequence.get('_sequence').get('name');
      for (const model of Array.from(primers.models)) {
        for (const direction of ['forward', 'reverse', 'probe'] as Array<Direction>) {
          if (model.get(direction) != null) {
            const match = model.get(direction);
            const name = biojs.SequencePrimer.generateName(
              filename,
              match.start,
              match.end,
              direction,
              sequence.length(),
              sequence.startIndex()
            );
            model.set(
              direction,
              new biojs.SequencePrimer({
                name,
                bases: match.sequence,
                color: colors.defaultPastelColor,
                start: match.start,
                end: match.end,
                strand: direction === 'reverse' ? -1 : 1,
              })
            );
          }
        }
      }
      const tab = new Primer3TabState({sequence, primers});
      this.model.get('buffers').addTab(tab);
    }
  }

  generatePrimers(): void {
    const sequence = this.model.get('sequence');
    analytics.track('primer', 'generate_primers_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequence.get('id'),
    });
    let params;
    this.working = true;
    this.render();
    this.model.savePreferences();

    const onFinish = (): void => {
      this.working = false;
      this.render();
    };

    try {
      params = this.model.getParams();
    } catch (e) {
      if (e instanceof TypeError) {
        onFinish();
        notifications.error(e.message);
        return;
      }
      throw e;
    }

    const prefs = preferences.get(PrimerPreferences.MELTING_TEMP_DNA);
    const formula = prefs.method === MeltingTempMethod.primer3 ? 1 : 0;

    function getOrElse(name: string, defaultVal: number): number {
      const val = prefs[name];
      if (val != null) {
        return val;
      } else {
        return defaultVal;
      }
    }

    // TODO: unify where these Tm defaults are stored
    const extraParams = {
      sequence: sequence.bases(),
      PRIMER_SALT_MONOVALENT: getOrElse('monovalentConc', 50.0),
      // Only pass divalent salt and dNTP concentration if using primer3 to mirror our own melting temp
      // calculations. See client/biojs/Util.ts breslauerTm.
      PRIMER_SALT_DIVALENT: prefs.method === MeltingTempMethod.primer3 ? getOrElse('divalentConc', 0.0) : 0.0,
      PRIMER_DNTP_CONC: prefs.method === MeltingTempMethod.primer3 ? getOrElse('dntpConc', 0.0) : 0.0,
      PRIMER_DNA_CONC: getOrElse('dnaConc', 250.0),
      PRIMER_TM_FORMULA: formula,
    };
    TaskRunner.runTask({
      url: '/1/api/primer3',
      type: 'PUT',
      contentType: 'application/json',
      // TODO: refactor _.extend to be type-explicit
      // eslint-disable-next-line no-restricted-syntax
      data: JSON.stringify(_.extend(params, extraParams)),
    })
      .done(this.addPrimers)
      .fail((res) => {
        this.trackGeneratePrimersFail(notifications.getFriendlyError(res));
        notifications.notifyError(res);
      })
      .always(onFinish);
  }

  override render(): this {
    this.reactRoot = renderToDOM(
      <PrimerWizardOptions generatePrimers={this.generatePrimers} disabled={this.working ?? false} />,
      this.el,
      this.reactRoot
    );
    return this;
  }

  override removeAsync(callback: () => void): this {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }
}
/* eslint-enable @typescript-eslint/unbound-method */
PrimerWizardOptionsView.initClass();

export {PrimerWizardOptionsView as OptionsView, PrimerWizardTabView as TabView};
